import React, { useState, useEffect } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, KeyRound, Copy, RefreshCw, Server, Plug, ChevronDown, ShieldAlert } from "lucide-react";
import { http } from "@/lib/api";
import { TextField, SelectField, SwitchField, TextareaField } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";
import { RolesMatrix } from "@/components/settings/RolesMatrix";
import { Globe, Check, Trash2, Plus } from "lucide-react";

// --- Super Admin: Platform Domains — one active domain drives every admin/portal/tracking link ---
export function PlatformDomains() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["platform-domains"], queryFn: async () => (await http.get("/platform-domains")).data });
  const [val, setVal] = useState("");
  const items = data?.items || [];
  const active = items.find((d) => d.is_active);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["platform-domains"] }); qc.invalidateQueries({ queryKey: ["platform-public"] }); qc.invalidateQueries({ queryKey: ["platform-settings"] }); };

  const add = async () => {
    if (!val.trim()) return;
    try { await http.post("/platform-domains", { domain: val.trim() }); setVal(""); refresh(); toast.success("Platform domain added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const activate = async (d) => {
    try { await http.post(`/platform-domains/${d.id}/activate`); refresh(); qc.invalidateQueries({ queryKey: ["public-platform-hosts"] }); qc.invalidateQueries({ queryKey: ["public-branding"] }); toast.success(`Primary platform domain: ${d.domain}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deactivate = async (d) => {
    try {
      const r = (await http.post(`/platform-domains/${d.id}/deactivate`)).data;
      refresh(); qc.invalidateQueries({ queryKey: ["public-platform-hosts"] }); qc.invalidateQueries({ queryKey: ["public-branding"] }); qc.invalidateQueries({ queryKey: ["platform-public"] });
      toast.success(r.promoted ? `${d.domain} → Standby · new Primary: ${r.promoted}` : `${d.domain} → Standby — reverted to the live host`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (d) => {
    try { await http.delete(`/platform-domains/${d.id}`); refresh(); toast.success("Removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const verify = async (d) => {
    try {
      const r = (await http.post(`/platform-domains/${d.id}/verify`)).data;
      refresh();
      if (r.status === "verified") toast.success(`${d.domain} verified — ready to Set active`);
      else if (r.status === "pending") toast.warning(`${d.domain}: ${r.message || "DNS OK, SSL not ready yet"}`);
      else toast.error(`${d.domain} failed — ${r.message || "check DNS/SSL/proxy"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };
  const statusBadge = (d) => {
    if (d.is_active) return ["Primary", "#22C55E"];
    if (d.status === "verified") return ["Standby", "#3B82F6"];
    if (d.status === "failed") return ["Verification Failed", "#EF4444"];
    if (d.dns_status === "ok") return ["DNS OK · SSL Pending", "#EAB308"];
    return ["Pending", "#EAB308"];
  };
  const subStatus = (d) => {
    if (d.is_ip) return "IP host · HTTP (no SSL)";
    const dns = d.dns_status === "ok" ? "DNS OK" : d.dns_status === "failed" ? "DNS Failed" : "DNS Pending";
    const ssl = d.ssl_status === "active" ? "SSL Active" : d.ssl_status === "failed" ? "SSL Failed" : d.ssl_status === "n/a" ? "" : "SSL Pending";
    return ["domain", dns, ssl].filter(Boolean).join(" · ");
  };

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6 space-y-4" data-testid="platform-domains-manager">
      <div>
        <h3 className="text-sm font-medium text-foreground">Platform domains</h3>
        <p className="mt-1 text-xs text-muted-foreground">The domain the whole platform is served on. Exactly one is <span className="text-foreground">Active</span> — admin & every tenant portal link are built from it. Switching domains never changes the URL structure (only the domain).</p>
      </div>
      <div className="flex items-end gap-2">
        <TextField label="Add platform domain (or IP)" value={val} onChange={setVal} testId="platform-domain-input" className="flex-1" placeholder="e.g. panel.affilix.com or 94.156.232.136" />
        <Button onClick={add} data-testid="platform-domain-add" className="h-10 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <div className="space-y-2" data-testid="platform-domains-list">
        {items.length === 0 && <p className="text-xs text-muted-foreground">No platform domains yet — the live host is used automatically until you add one.</p>}
        {items.map((d) => {
          const [label, color] = statusBadge(d);
          return (
          <div key={d.id} className={`rounded-lg border px-3 py-2.5 ${d.is_active ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-white/[0.08] bg-[#0A0A0A]"}`} data-testid={`platform-domain-row-${d.domain}`}>
            <div className="flex items-center gap-3">
              <Globe className={`h-4 w-4 shrink-0 ${d.is_active ? "text-[#22C55E]" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <code className="block truncate font-mono text-xs text-foreground">{d.scheme || (d.is_ip ? "http" : "https")}://{d.domain}</code>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{subStatus(d)}</span>
              </div>
              <span className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider" style={{ background: `${color}22`, color }}>{label}</span>
              <button onClick={() => verify(d)} data-testid={`platform-domain-verify-${d.domain}`} className="rounded-md border border-white/15 px-2.5 py-1 text-xs text-foreground hover:bg-white/5">Verify</button>
              {!d.is_active && d.status === "verified" && <button onClick={() => activate(d)} data-testid={`platform-domain-activate-${d.domain}`} className="rounded-md border border-[#22C55E]/40 bg-[#22C55E]/10 px-2.5 py-1 text-xs text-[#22C55E] hover:bg-[#22C55E]/20"><Check className="mr-1 inline h-3 w-3" />Set as Primary</button>}
              {!d.is_active && d.status !== "verified" && <span title="Verify the domain first (DNS + SSL) before it can become the Primary Platform Domain" className="rounded-md border border-white/10 px-2.5 py-1 text-xs text-muted-foreground/50 cursor-not-allowed"><Check className="mr-1 inline h-3 w-3" />Set as Primary</span>}
              {d.is_active && <button onClick={() => deactivate(d)} data-testid={`platform-domain-deactivate-${d.domain}`} className="rounded-md border border-white/15 px-2.5 py-1 text-xs text-foreground hover:bg-white/5">Deactivate</button>}
              {!d.is_active && <button onClick={() => remove(d)} data-testid={`platform-domain-delete-${d.domain}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            {!d.is_active && d.status === "pending" && !d.is_ip && (
              <p className="mt-2 pl-7 text-[11px] text-muted-foreground">Point an <span className="text-foreground">A record</span> to this server's IP (or a <span className="text-foreground">CNAME</span> to your platform host), set up SSL + reverse proxy on your server, then click <span className="text-foreground">Verify</span>. The platform keeps serving on the current Active domain until you press Set active.</p>
            )}
            {!d.is_active && d.status === "failed" && (
              <p className="mt-2 pl-7 text-[11px] text-[#EF4444]/80">Verification failed — check DNS records, SSL certificate and reverse proxy, then click Verify again.</p>
            )}
          </div>
        );})}
      </div>
      {active && (
        <div className="rounded-lg border border-[#22C55E]/30 bg-[#22C55E]/[0.06] p-4 space-y-2.5" data-testid="active-domain-panel">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#22C55E]">
            <Globe className="h-3.5 w-3.5" /> Primary platform domain
          </div>
          {(() => {
            const sc = active.scheme || (active.is_ip ? "http" : "https");
            const origin = `${sc}://${active.domain}`;
            const rows = [
              ["Admin", `${origin}/login`],
              ["Affiliate Portal", `${origin}/portal/{slug}`],
              ["Tracking", `${origin}/api/r/{key}`],
              ["Postback", `${origin}/api/postback`],
            ];
            return (
              <div className="space-y-1.5">
                <code className="block font-mono text-sm text-foreground">{origin}</code>
                <div className="mt-2 grid gap-1.5">
                  {rows.map(([label, url]) => (
                    <div key={label} className="flex items-center gap-2 text-xs">
                      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
                      <code className="flex-1 truncate font-mono text-foreground" title={url}>{url}</code>
                      <button onClick={() => copyText(url, `${label} URL copied`)} className="shrink-0 text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Every domain is <span className="text-foreground">used for</span>: Admin · Tenant portals · Tracking · Postbacks. Switching the active domain changes only the domain — all URL patterns above stay the same.</p>
    </div>
  );
}

// --- Super Admin: platform general + white-label domain config (persisted) ---
export function PlatformSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await http.get("/platform-settings")).data });
  const [g, setG] = useState(null);
  const [showRbac, setShowRbac] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  useEffect(() => { if (data) setG(data); }, [data]);
  const set = (k, v) => setG((s) => ({ ...s, [k]: v }));
  const save = async () => {
    await http.put("/platform-settings", g);
    qc.invalidateQueries({ queryKey: ["platform-settings"] });
    qc.invalidateQueries({ queryKey: ["platform-public"] });
    toast.success("Platform settings saved");
  };
  if (!g) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="max-w-3xl space-y-5" data-testid="platform-settings">
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6 space-y-2">
        <h3 className="text-sm font-medium text-foreground">General</h3>
        <p className="text-xs text-muted-foreground">Platform name, logo & public look live in the <span className="text-foreground">Branding</span> tab. Currency is chosen once per organization at creation; timezone follows each user's device/profile. This tab holds technical platform settings only.</p>
      </div>

      <PlatformDomains />

      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6 space-y-3" data-testid="cname-target-card">
        <div className="flex items-center gap-2.5"><Globe className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Tenant custom domains — CNAME target</h3></div>
        <p className="text-xs text-muted-foreground">Tenant Partner Portal / Tracking / Postback domains must point their DNS <span className="text-foreground">CNAME</span> to this host.</p>
        <TextField label="White-label CNAME target" value={g.cname_target} onChange={(v) => set("cname_target", v)} testId="platform-cname" placeholder="trk.tracify.top" />
      </div>

      <Button onClick={save} data-testid="platform-save" className="bg-white text-black hover:bg-white/90">Save changes</Button>

      {/* Advanced — legacy / optional domain configuration, hidden so it doesn't confuse the primary flow */}
      <div className="rounded-xl border border-white/[0.08] bg-[#141417]">
        <button onClick={() => setShowAdvanced((v) => !v)} data-testid="advanced-domains-toggle"
          className="flex w-full items-center justify-between px-6 py-4 text-left">
          <div className="flex items-center gap-2.5"><Server className="h-4 w-4 text-muted-foreground" />
            <div><div className="text-sm font-medium text-foreground">Compatibility / legacy only</div><div className="text-xs text-muted-foreground">Optional dedicated tracking subdomain & legacy fallbacks. Not needed for the standard flow above.</div></div></div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>
        {showAdvanced && (
          <div className="border-t border-white/[0.08] p-6 space-y-5">
            <div className="space-y-3" data-testid="platform-core-domains">
              <h3 className="text-sm font-medium text-foreground">Dedicated tracking domain (optional)</h3>
              <TextField label="Base tracking domain" value={g.base_tracking_domain} onChange={(v) => set("base_tracking_domain", v)} testId="platform-base-tracking-domain" placeholder="e.g. track.yourdomain.com (blank = use active platform domain)" />
              <p className="text-xs text-muted-foreground">Optional separate subdomain for clean short links <span className="text-foreground">track.yourdomain.com/{"{key}"}</span>. If blank, tracking runs on the active platform domain via <span className="text-foreground">/api/r/</span> and <span className="text-foreground">/api/postback</span>.</p>
            </div>
            <div className="space-y-3" data-testid="platform-domains">
              <h3 className="text-sm font-medium text-foreground">Legacy fallbacks</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField label="Affiliate portal base domain" value={g.affiliate_portal_base} onChange={(v) => set("affiliate_portal_base", v)} testId="platform-affiliate-portal-base" placeholder="yourdomain.com" />
                <TextField label="Default tracking domain (legacy)" value={g.tracking_domain} onChange={(v) => set("tracking_domain", v)} testId="platform-tracking-domain" />
                <TextField label="Platform fallback domain (legacy)" value={g.fallback_domain} onChange={(v) => set("fallback_domain", v)} testId="platform-fallback" />
              </div>
              <TextareaField label="Domain connection instructions (shown to tenants)" value={g.domain_instructions} onChange={(v) => set("domain_instructions", v)} testId="platform-instructions" />
            </div>
            <Button onClick={save} data-testid="platform-save-advanced" className="bg-white text-black hover:bg-white/90">Save changes</Button>
          </div>
        )}
      </div>

      {/* Advanced system RBAC — hidden behind a disclosure, not the primary UX */}
      <div className="rounded-xl border border-white/[0.08] bg-[#141417]">
        <button onClick={() => setShowRbac((v) => !v)} data-testid="advanced-rbac-toggle"
          className="flex w-full items-center justify-between px-6 py-4 text-left">
          <div className="flex items-center gap-2.5"><ShieldAlert className="h-4 w-4 text-muted-foreground" />
            <div><div className="text-sm font-medium text-foreground">Advanced — System RBAC</div><div className="text-xs text-muted-foreground">Global role permission defaults (engine-level). Tenant access is managed by each Tenant Admin.</div></div></div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showRbac ? "rotate-180" : ""}`} />
        </button>
        {showRbac && <div className="border-t border-white/[0.08] p-6"><RolesMatrix /></div>}
      </div>
    </div>
  );
}

// --- Super Admin: API keys (scaffold) ---
export function ApiSettings() {
  const [key, setKey] = useState("sk_live_" + "x".repeat(24));
  const copy = () => copyText(key);
  const regen = () => { setKey("sk_live_" + Math.random().toString(36).slice(2).padEnd(24, "x").slice(0, 24)); toast.success("New key generated"); };
  return (
    <div className="max-w-2xl space-y-4" data-testid="api-settings">
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-3 flex items-center gap-2.5"><KeyRound className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Platform API key</h3></div>
        <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
          <code className="flex-1 truncate font-mono text-xs text-foreground">{key}</code>
          <button onClick={copy} className="text-muted-foreground hover:text-foreground"><Copy className="h-4 w-4" /></button>
        </div>
        <Button onClick={regen} variant="outline" data-testid="api-regen" className="mt-3 gap-2 border-white/15 text-xs"><RefreshCw className="h-3.5 w-3.5" /> Regenerate</Button>
        <p className="mt-3 text-xs text-muted-foreground">Used for programmatic platform access. Full API gateway is on the roadmap.</p>
      </div>
    </div>
  );
}

// --- Super Admin: system (scaffold) ---
export function SystemSettings() {
  const [maintenance, setMaintenance] = useState(false);
  const info = [["Version", "v1.0 · Stage 2"], ["Environment", "Production-preview"], ["Database", "MongoDB (managed)"], ["Billing engine", "Active · crypto stub"]];
  return (
    <div className="max-w-2xl space-y-4" data-testid="system-settings">
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-4 flex items-center gap-2.5"><Server className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">System information</h3></div>
        <div className="space-y-2">
          {info.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="text-foreground">{v}</span></div>
          ))}
        </div>
      </div>
      <SwitchField label="Maintenance mode" hint="Temporarily restrict access for maintenance" checked={maintenance} onChange={(v) => { setMaintenance(v); toast.success(v ? "Maintenance mode on" : "Maintenance mode off"); }} testId="system-maintenance" />
    </div>
  );
}

// --- Tenant Admin: integrations (scaffold) ---
export function IntegrationsSettings() {
  const items = [
    { name: "Postback / S2S", desc: "Server-to-server conversion postbacks", icon: Plug },
    { name: "Tracker API", desc: "Connect an external tracker or DSP", icon: Plug },
  ];
  return (
    <div className="max-w-2xl space-y-3" data-testid="integrations-settings">
      {items.map((it) => (
        <div key={it.name} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#141417] p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/[0.05]"><it.icon className="h-5 w-5 text-muted-foreground" /></span>
          <div className="flex-1"><div className="text-sm font-medium text-foreground">{it.name}</div><div className="text-xs text-muted-foreground">{it.desc}</div></div>
          <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-xs text-muted-foreground">Coming soon</span>
        </div>
      ))}
    </div>
  );
}

// --- generic link tab to an existing page (no UI duplication) ---
export function LinkTab({ to, title, desc, testId }) {
  return (
    <Link to={to} data-testid={testId} className="flex max-w-xl items-center gap-4 rounded-xl border border-white/[0.08] bg-[#141417] p-6 transition-colors hover:border-white/20">
      <div className="flex-1"><div className="text-sm font-medium text-foreground">{title}</div><div className="mt-1 text-sm text-muted-foreground">{desc}</div></div>
      <ArrowRight className="h-5 w-5 text-muted-foreground" />
    </Link>
  );
}
